import { type Translate } from "@redotech/redo-i18n";
import {
  ShippingClaimDenialReason,
  ShippingClaimReason,
  ShippingClaimRequestedBy,
  ShippingClaimStatus,
  ShippingClaimType,
  toMerchantDenialReason,
} from "@redotech/redo-package-protection-common/shipping-claims/shipping-claim";

export function getMerchantFacingClaimStatusLabel(
  status: string,
  t: Translate,
): string {
  switch (status) {
    case ShippingClaimStatus.REQUESTED:
      return t("Requested");
    case ShippingClaimStatus.SUBMITTED:
      return t("Submitted to carrier");
    case ShippingClaimStatus.PENDING_DOCS:
      return t("Pending documentation");
    case ShippingClaimStatus.CARRIER_UNSUBMITTED_MERCHANT_APPROVED:
    case ShippingClaimStatus.CARRIER_SUBMITTED_MERCHANT_APPROVED:
    case ShippingClaimStatus.APPROVED:
      return t("Approved");
    case ShippingClaimStatus.REJECTED:
      return t("Rejected");
    default:
      return status;
  }
}

export function getShippingClaimReasonLabel(
  reason: string,
  t: Translate,
): string {
  switch (reason) {
    case ShippingClaimReason.LATE:
      return t("Late", { context: "shipping claim reason" });
    case ShippingClaimReason.DAMAGED:
      return t("Damaged", { context: "shipping claim reason" });
    case ShippingClaimReason.LOST:
      return t("Lost", { context: "shipping claim reason" });
    case ShippingClaimReason.STOLEN:
      return t("Stolen", { context: "shipping claim reason" });
    default:
      return reason;
  }
}

export function getShippingClaimTypeLabel(type: string, t: Translate): string {
  switch (type) {
    case ShippingClaimType.OUTBOUND:
      return t("Outbound");
    case ShippingClaimType.RETURN:
      return t("Return", { context: "shipping claim type" });
    default:
      return type;
  }
}

export function getShippingClaimRequestedByLabel(
  requestedBy: string,
  t: Translate,
): string {
  switch (requestedBy) {
    case ShippingClaimRequestedBy.MERCHANT:
      return t("Merchant");
    case ShippingClaimRequestedBy.PROACTIVE:
      return t("Proactive");
    default:
      return requestedBy;
  }
}

export function getMerchantDenialExplanation(
  reason: string | null | undefined,
  t: Translate,
): { label: string; description: string } {
  switch (toMerchantDenialReason(reason)) {
    case ShippingClaimDenialReason.PACKAGE_DELIVERED:
      return {
        label: t("Package delivered"),
        description: t(
          "The carrier's records show this package was delivered.",
        ),
      };
    case ShippingClaimDenialReason.INSUFFICIENT_DOCUMENTATION:
      return {
        label: t("Missing documentation"),
        description: t(
          "The carrier needed more documentation to approve this claim. If you have supporting documents, contact Redo support.",
        ),
      };
    case ShippingClaimDenialReason.VALUE_NOT_SUBSTANTIATED:
      return {
        label: t("Value not verified"),
        description: t(
          "The carrier couldn't verify the value of this shipment. If you have an invoice or receipt, contact Redo support.",
        ),
      };
    case ShippingClaimDenialReason.PROOF_OF_DELIVERY_MISSING:
      return {
        label: t("Proof of delivery missing"),
        description: t(
          "The carrier required proof of delivery that wasn't available.",
        ),
      };
    case ShippingClaimDenialReason.NOT_COVERED_BY_SERVICE_LEVEL:
      return {
        label: t("Not covered by service"),
        description: t(
          "The shipping service on this label doesn't cover this type of claim.",
        ),
      };
    case ShippingClaimDenialReason.OUTSIDE_FILING_WINDOW:
    case ShippingClaimDenialReason.DUPLICATE_CLAIM:
    case ShippingClaimDenialReason.CLAIM_ALREADY_PAID:
    case ShippingClaimDenialReason.OTHER:
      return {
        label: t("Denied by carrier"),
        description: t("The carrier denied this claim."),
      };
  }
}
